import React, { useState, useRef } from "react";
import {
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { RiDeleteBinLine } from "react-icons/ri";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useGetFaqs from "../../../hooks/catalog/faqs/useGetFaqs";
import useDeleteFaq from "../../../hooks/catalog/faqs/useDeleteFaq";
import CreateFaqModal from "./CreateFaqModal";
import EditFaqModal from "./EditFaqModal";
import Spinner from "../../../components/spinner";
import Errors from "../../errors";
import Pagination from "../../../components/elements/pagination";
import DeleteModal from "../../../components/elements/deleteModal";
import { debounce } from "lodash";
import SwitchToggle from "../../../components/elements/switchtoggle/SwitchToggle";
import toast from "react-hot-toast";
import useCreate from "../../../hooks/useCreate";

const FaqList = () => {
  const {
    data,
    handlePageClick,
    handleSearchVal,
    paramsObject,
    handleSetLimit,
  } = useGetFaqs();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState(null);
  const [searchValue, setSearchValue] = useState(
    paramsObject.searchQuery || ""
  );

  const {
    faqId,
    setFaqId,
    isDeleteModalOpen,
    setDeleteModalOpen,
    handleSubmit: handleDeleteSubmit,
  } = useDeleteFaq();

  const debouncedOnSearch = useRef(
    debounce((val) => {
      handleSearchVal({ target: { value: val } });
    }, 500)
  ).current;

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
    debouncedOnSearch(e.target.value);
  };

  const handleDelete = (id) => {
    setFaqId(id);
    setDeleteModalOpen(true);
  };

  const handleEdit = (id) => {
    setSelectedFaqId(id);
    setEditModalOpen(true);
  };

  return (
    <div className="category px-4">
      <div className="category__head headingBorder__b">
        <h2 className="titleHeading">FAQs</h2>
      </div>

      <div className="py-5 space-y-5">
        {/* Controls Row */}
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div className="min-w-[21rem] md:w-auto md:flex-1">
            <input
              onChange={handleInputChange}
              value={searchValue}
              type="text"
              placeholder="Search FAQs..."
              className="input"
            />
          </div>
          <div>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              className="primaryBtn-outlined"
              onClick={() => setCreateModalOpen(true)}
            >
              Add FAQ
            </Button>
          </div>
          <div>
            <label htmlFor="" className="label">
              Limit
            </label>
            <select
              name="limit"
              value={data.data?.per_page || paramsObject.limit}
              className="input"
              onChange={handleSetLimit}
            >
              {Array(5)
                .fill(1)
                .map((_, idx) => {
                  return (
                    <option key={idx} value={(idx + 1) * 10}>
                      {(idx + 1) * 10}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {/* FAQs Accordion List */}
        {data.isLoading || data.isFetching ? (
          <Spinner />
        ) : data.status === "error" ? (
          <Errors errorObject={data.error} inline />
        ) : (
          <div className="space-y-4">
            {data.data?.data?.length === 0 ? (
              <div className="text-center py-8 text-gray-500 font-medium bg-white rounded-lg shadow">
                No FAQs Found
              </div>
            ) : (
              data.data?.data?.map((faq, index) => (
                <Accordion key={faq.faq_id} className="shadow-md">
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    className="pr-2"
                  >
                    <div className="flex flex-wrap items-center gap-5 justify-between w-full pr-2">
                      <div className="font-medium text-gray-800 w-full md:w-auto flex-1">
                        {faq.question}
                      </div>
                      <div
                        className="flex items-center ml-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaqStatusDiv data={faq} />
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(faq.faq_id)}
                          className="text-blue-600"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(faq.faq_id)}
                          className="text-red-600"
                        >
                          <RiDeleteBinLine fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className="border-t pt-4 bg-gray-50">
                    <p className="text-gray-700">{faq.answer}</p>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </div>
        )}

        {/* Pagination */}
        <div>
          <Pagination
            currentPage={+paramsObject.pageNo}
            lengthofItems={data.data?.total_count}
            limit={paramsObject.limit}
            onPageChange={handlePageClick}
            pageCount={Math.ceil(
              (data.data?.total_count || 0) / (data.data?.per_page || 10)
            )}
          />
        </div>
      </div>

      {/* Create FAQ Modal */}
      <CreateFaqModal
        open={createModalOpen}
        handleClose={() => setCreateModalOpen(false)}
      />

      {/* Edit FAQ Modal */}
      {selectedFaqId && editModalOpen && (
        <EditFaqModal
          open={editModalOpen}
          handleClose={() => {
            setEditModalOpen(false);
            setSelectedFaqId(null);
          }}
          faqId={selectedFaqId}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title={"Delete FAQ"}
        message={"Are you sure you want to delete this FAQ?"}
        onDelete={() => handleDeleteSubmit({ faq_id: faqId })}
      />
    </div>
  );
};

export default FaqList;

const FaqStatusDiv = ({ data }) => {
  const [status, setStatus] = useState(data.status);

  const { setDataToServer: updateStatusReq } = useCreate({
    url: "faqs/update_status",
    onSuccess: () => {
      toast.success("FAQ Status Updated Successfully", {
        position: "top-right",
        duration: 3000,
      });
    },
    onError: () => {
      toast.error("Failed to Update FAQ Status", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  const handleStatusChange = (e) => {
    const newStatus = e.target.checked;
    setStatus(newStatus);
    updateStatusReq.mutate({
      faq_id: data.faq_id,
      status: newStatus,
    });
  };

  return (
    <div className="flex items-center justify-center mr-16">
      <SwitchToggle
        label="Status: "
        checkedValue={status}
        onChange={(e) => handleStatusChange(e)}
      />
    </div>
  );
};
